import React, { useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import { ThemeContext } from "../context/ThemeContext";

const makeStyles = (themeColors: any): Record<string, React.CSSProperties> => ({
  page: {
    minHeight: "100vh",
    padding: "44px 18px",
    background: themeColors.bg,
    color: themeColors.text,
    fontFamily: "Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
  },
  shell: {
    maxWidth: 1100,
    margin: "0 auto",
  },
  hero: {
    border: `1px solid ${themeColors.border}`,
    background: themeColors.panel,
    borderRadius: 30,
    padding: "34px clamp(20px, 4vw, 48px)",
    boxShadow: themeColors.glow,
    backdropFilter: "blur(18px)",
    marginBottom: 22,
    overflow: "hidden",
    position: "relative",
  },
  glow: {
    position: "absolute",
    inset: "-80px -120px auto auto",
    width: 260,
    height: 260,
    borderRadius: "50%",
    background: `${themeColors.accent}21`,
    filter: "blur(40px)",
    pointerEvents: "none",
  },
  eyebrow: {
    color: themeColors.accent2,
    letterSpacing: 2.4,
    textTransform: "uppercase",
    fontSize: 12,
    fontWeight: 900,
    marginBottom: 10,
  },
  title: {
    fontSize: "clamp(34px, 5vw, 58px)",
    margin: "0 0 12px",
    color: themeColors.text,
    lineHeight: 1.02,
  },
  intro: {
    color: themeColors.mutedText,
    fontSize: 17,
    lineHeight: 1.75,
    maxWidth: 880,
    margin: 0,
  },
  nav: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 24,
  },
  link: {
    color: themeColors.accent2,
    textDecoration: "none",
    fontWeight: 850,
    border: `1px solid ${themeColors.border}`,
    background: themeColors.buttonBg,
    padding: "10px 14px",
    borderRadius: 999,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 18,
    alignItems: "start",
  },
  card: {
    border: `1px solid ${themeColors.border}`,
    background: themeColors.cardBg,
    borderRadius: 24,
    padding: 22,
    boxShadow: "0 14px 42px rgba(0,0,0,0.28)",
    minWidth: 0,
    alignSelf: "start",
    height: "fit-content",
  },
  bigCard: {
    gridColumn: "1 / -1",
    border: `1px solid ${themeColors.border}`,
    background: themeColors.panel,
    borderRadius: 26,
    padding: 24,
    boxShadow: "0 18px 54px rgba(0,0,0,0.34)",
  },
  h2: {
    color: themeColors.accent,
    fontSize: 21,
    margin: "0 0 10px",
  },
  version: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    marginTop: 10,
    padding: "10px 14px",
    borderRadius: 999,
    color: themeColors.bg,
    background: `linear-gradient(90deg, ${themeColors.accent2}, ${themeColors.text})`,
    fontWeight: 950,
    letterSpacing: 1.4,
    boxShadow: themeColors.glow,
  },
  p: {
    color: themeColors.mutedText,
    fontSize: 15.5,
    lineHeight: 1.72,
    margin: "8px 0",
  },
  list: {
    color: themeColors.text,
    lineHeight: 1.8,
    paddingLeft: 20,
    margin: "10px 0 0",
  },
  badge: {
    display: "inline-block",
    color: themeColors.accent2,
    border: `1px solid ${themeColors.border}`,
    background: themeColors.buttonBg,
    borderRadius: 999,
    padding: "6px 10px",
    fontSize: 12,
    fontWeight: 850,
    marginBottom: 10,
  },
  footer: {
    marginTop: 26,
    color: themeColors.mutedText,
    fontSize: 13,
    lineHeight: 1.6,
    textAlign: "center",
  },
});

export default function System() {
  const { themeColors } = useContext(ThemeContext);
  const styles = useMemo(() => makeStyles(themeColors), [themeColors]);

  return (
    <main style={styles.page}>
      <div style={styles.shell}>
        <section style={styles.hero}>
          <div style={styles.glow} />

          <div style={styles.eyebrow}>AVAAIO SYSTEM CORE</div>

          <h1 style={styles.title}>
            ⚙️ AVAAIO System Center
          </h1>

          <p style={styles.intro}>
            Explore Ava’s current capabilities, learn how to use device-powered
            features, review your privacy controls, and see what AVAAIO™ is
            preparing for its next release.
          </p>

          <p style={{ ...styles.p, marginTop: 14 }}>
            This is the user-facing command center for Ava’s connected AI
            experience. It is informational and is not an internal diagnostic
            or administrative console.
          </p>

          <nav style={styles.nav}>
            <Link style={styles.link} to="/info">Info Center</Link>
            <Link style={styles.link} to="/support">Support</Link>
            <Link style={styles.link} to="/profile">Profile</Link>
            <Link style={styles.link} to="/account">Account</Link>
            <Link style={styles.link} to="/privacy-policy">Privacy</Link>
            <Link style={styles.link} to="/terms-and-conditions">Terms</Link>
          </nav>
        </section>

        <section style={styles.grid}>
          <article style={styles.bigCard}>
            <div style={styles.badge}>CURRENT FOUNDATION</div>

            <h2 style={styles.h2}>AVAAIO Platform Version</h2>

            <div style={styles.version}>GENESIS 1.0</div>

            <p style={{ ...styles.p, marginTop: 16 }}>
              GENESIS 1.0 is Ava’s current foundation experience. It brings
              together conversational assistance, voice interaction,
              user-controlled memory, Ava Vision, supported device-motion
              response, education, games, entertainment, account controls,
              support resources, and legal guidance.
            </p>
          </article>

          <article style={styles.bigCard}>
            <div style={styles.badge}>START HERE</div>

            <h2 style={styles.h2}>How to Use Ava</h2>

            <p style={styles.p}>
              Type a message into Ava’s chat box and select Send. Ava can help
              with questions, explanations, writing, learning, planning,
              creativity, and everyday assistance.
            </p>

            <p style={styles.p}>
              Select the microphone control to speak with Ava. Your browser,
              phone, or app may request microphone permission before listening
              can begin.
            </p>

            <p style={styles.p}>
              Ava’s Vision and Motion controls are separate. Vision helps Ava
              interpret the active camera view. Motion lets Ava visually react
              to supported phone or tablet movement.
            </p>
          </article>

          <article style={styles.card}>
            <div style={styles.badge}>CHAT + VOICE</div>

            <h2 style={styles.h2}>Talk With Ava</h2>

            <ul style={styles.list}>
              <li>Type into the chat box for normal conversations.</li>
              <li>Select Mic to begin voice input.</li>
              <li>Your device may ask for microphone permission.</li>
              <li>Turn Mic off to stop new voice capture.</li>
              <li>Mute controls Ava’s spoken response output.</li>
              <li>
                Voice availability may depend on the device, browser, region,
                account access, and configured speech service.
              </li>
            </ul>
          </article>

          <article style={styles.card}>
            <div style={styles.badge}>AVA VISION</div>

            <h2 style={styles.h2}>Use Ava’s Visual Awareness</h2>

            <p style={styles.p}>
              Ava Vision never automatically enables the camera. You must
              activate Ava’s Vision control and approve camera permission.
            </p>

            <p style={styles.p}>
              While Vision is active, camera frames may be processed by the
              AVAAIO Vision service for current scene analysis and supported
              recent visual context.
            </p>

            <ul style={styles.list}>
              <li>Use Vision only when you want Ava to examine the camera view.</li>
              <li>Keep sensitive documents and private information out of view.</li>
              <li>Improve lighting and hold the device steady for better results.</li>
              <li>Turn Vision off when it is no longer needed.</li>
              <li>
                Vision availability depends on account access, permission,
                browser support, and device capability.
              </li>
            </ul>

            <p style={styles.p}>
              Ava Vision may make mistakes and must not be used as the sole
              source for identification, surveillance, emergencies, medical
              decisions, security decisions, or other safety-critical tasks.
            </p>
          </article>

          <article style={styles.card}>
            <div style={styles.badge}>DEVICE MOTION</div>

            <h2 style={styles.h2}>Let Ava Follow Device Movement</h2>

            <p style={styles.p}>
              On supported mobile devices, Ava can visually respond when the
              phone or tablet tilts or changes direction.
            </p>

            <ul style={styles.list}>
              <li>Ava may look left, right, upward, or downward.</li>
              <li>Motion requires a supported orientation sensor.</li>
              <li>Apple devices may display a Motion and Orientation permission request.</li>
              <li>Motion does not automatically turn on the camera.</li>
              <li>The feature can be disabled from Ava’s controls.</li>
              <li>Availability depends on device support, applicable permissions, and current AVAAIO feature availability.</li>
            </ul>
          </article>

          <article style={styles.card}>
            <div style={styles.badge}>PRIVACY CONTROLS</div>

            <h2 style={styles.h2}>You Control Device Permissions</h2>

            <ul style={styles.list}>
              <li>Camera access requires your permission.</li>
              <li>Microphone access requires your permission.</li>
              <li>Device Motion may require your permission.</li>
              <li>Ava Vision remains off until activated by the user.</li>
              <li>Permissions can be revoked through browser or device settings.</li>
              <li>Standard text chat can operate without camera or motion access.</li>
            </ul>

            <p style={styles.p}>
              Only enable camera, microphone, or motion access when you
              understand why the feature needs it and you are comfortable using
              it in your current surroundings.
            </p>
          </article>

          <article style={styles.card}>
            <div style={styles.badge}>MEMORY</div>

            <h2 style={styles.h2}>Memory and Personalization</h2>

            <p style={styles.p}>
              Ava can use account-associated memory to provide more consistent
              and personalized assistance when memory learning is enabled.
            </p>

            <ul style={styles.list}>
              <li>Memory learning can be enabled or disabled from Profile.</li>
              <li>Saved profile information can be reviewed and corrected.</li>
              <li>A Clear Memory control is available from Profile.</li>
              <li>Account-deletion controls are available from Account.</li>
              <li>
                Do not give Ava passwords, payment-card numbers, private
                authentication codes, or government identification numbers.
              </li>
            </ul>
          </article>

          <article style={styles.card}>
            <div style={styles.badge}>ACCESS STATUS</div>

            <h2 style={styles.h2}>Current Account Access</h2>

            <ul style={styles.list}>
              <li>Existing approved users: Login available</li>
              <li>New public registration: Temporarily closed</li>
              <li>Profile and Account Centers: Active</li>
              <li>Support Center: Active</li>
              <li>Free and paid feature rules: Account controlled</li>
              <li>Paid-plan release controls: Under review</li>
            </ul>

            <p style={styles.p}>
              Registration is temporarily closed while AVAAIO prepares a safer
              early-access and public-release experience.
            </p>
          </article>

          <article style={styles.card}>
            <div style={styles.badge}>RECENT WORK</div>

            <h2 style={styles.h2}>Latest Upgrades</h2>

            <ul style={styles.list}>
              <li>Ava Vision now supports authenticated live-camera analysis.</li>
              <li>Ava can react to supported mobile-device orientation.</li>
              <li>Camera and motion features remain opt-in.</li>
              <li>Regular users receive simplified Vision guidance.</li>
              <li>Advanced Vision processing remains separate from the standard user display.</li>
              <li>Microphone permission guidance and offline voice handling were improved.</li>
              <li>Memory learning and memory-clearing controls were expanded.</li>
              <li>Chess, Checkers, and Education experiences were expanded.</li>
              <li>Account, privacy, legal, and support resources were improved.</li>
              <li>Public registration was temporarily closed while AVAAIO prepares its early-access and public-release experience.</li>
            </ul>
          </article>

          <article style={styles.card}>
            <div style={styles.badge}>SYSTEM PULSE</div>

            <h2 style={styles.h2}>Platform Capability Status</h2>

            <ul style={styles.list}>
              <li>Vision: Opt-in and account-gated</li>
              <li>Device Motion: Opt-in on supported devices</li>
              <li>Microphone: Permission controlled</li>
              <li>Memory: User controlled</li>
              <li>Account Center: Active</li>
              <li>Privacy and Legal Center: Active</li>
              <li>Registration: Temporarily closed</li>
            </ul>

            <p style={styles.p}>
              This section describes configured platform capability and is not
              a live engineering diagnostic report.
            </p>
          </article>

          <article style={styles.card}>
            <div style={styles.badge}>COMING NEXT</div>

            <h2 style={styles.h2}>Release Preparation</h2>

            <ul style={styles.list}>
              <li>Guided onboarding for chat, voice, Vision, and Motion.</li>
              <li>Verified-email early-access registration.</li>
              <li>Continued refinement of Free and Pro entitlement controls.</li>
              <li>Controlled subscription availability aligned with the public release.</li>
              <li>Expanded camera, microphone, and motion privacy explanations.</li>
              <li>Additional memory review and deletion controls.</li>
              <li>Improved accessibility and mobile permission guidance.</li>
              <li>Additional education and guided-writing tools.</li>
              <li>Continued refinement of mobile onboarding and release experiences.</li>
            </ul>
          </article>

          <article style={styles.bigCard}>
            <div style={styles.badge}>IMPORTANT SAFETY INFORMATION</div>

            <h2 style={styles.h2}>Use Ava Responsibly</h2>

            <p style={styles.p}>
              Ava is an artificial-intelligence system and can provide
              incorrect, incomplete, or outdated information.
            </p>

            <p style={styles.p}>
              Review important information independently. Do not rely on Ava as
              the only source for medical, legal, financial, emergency,
              security, identity, or safety-critical decisions.
            </p>
          </article>

          <article style={styles.bigCard}>
            <div style={styles.badge}>AVAAIO GENESIS</div>

            <h2 style={styles.h2}>Ava Is Evolving</h2>

            <p style={styles.p}>
              GENESIS is the opening foundation of AVAAIO. Ava is being
              developed as a more personal and useful assistant that combines
              conversation, voice, visual awareness, device interaction,
              learning tools, user-controlled memory, and everyday assistance.
            </p>

            <p style={styles.p}>
              Future updates are designed to make Ava easier to understand,
              safer to operate, more transparent about permissions, and more
              useful across mobile, desktop, learning, creativity, and daily
              life.
            </p>
          </article>
        </section>

        <footer style={styles.footer}>
          © 2026 American Globalo, LLC. All rights reserved.
          <br />
          AVAAIO™ is owned and operated by American Globalo, LLC.
          <br />
          Support: support@avaaio.com
        </footer>
      </div>
    </main>
  );
}
